import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";

/**
 * /trial?token=… — "Start my free trial" from the thank-you email sent to
 * students who started an application but did not submit it.
 *
 * Pressed by the student or a parent; neither is signed in, and this page never
 * signs anyone in. activate-trial sets up the trial (Step 1 project, 60-day
 * window) and emails the STUDENT a one-click sign-in link that asks them to
 * create a password — so a parent pressing it cannot enter the student's account.
 *
 * Plain fetch rather than functions.invoke, so the function's own sentences
 * ("This link is not valid…") reach the page.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";

async function call(
  body: Record<string, unknown>,
): Promise<{ ok: boolean; data: any }> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/activate-trial`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data?.ok !== false, data };
  } catch {
    return {
      ok: false,
      data: {
        error:
          "We could not reach the server. Check your connection and try again.",
      },
    };
  }
}

interface LoadResult {
  student_first: string;
  activated: boolean;
  email_hint: string;
}

export default function TrialAccessPage() {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token") ?? "",
    [],
  );
  const [info, setInfo] = useState<LoadResult | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<null | {
    kind: "sent" | "recent" | "enrolled";
  }>(null);

  const load = useCallback(async () => {
    if (!token) {
      setLoadErr(
        "This link is missing its code. Please use the button in your email.",
      );
      return;
    }
    const { ok, data } = await call({ action: "load", token });
    if (!ok) {
      setLoadErr(data?.error ?? "This link is not valid.");
      return;
    }
    setInfo(data as LoadResult);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  async function activate() {
    setBusy(true);
    setErr(null);
    const { ok, data } = await call({ action: "activate", token });
    setBusy(false);
    if (!ok) {
      setErr(data?.error ?? "We could not set up the trial. Please try again.");
      return;
    }
    if (data.already_enrolled) setDone({ kind: "enrolled" });
    else if (data.sent === false) setDone({ kind: "recent" });
    else setDone({ kind: "sent" });
  }

  if (loadErr) {
    return (
      <Shell>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5 }}>
          We could not open this link
        </Typography>
        <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {loadErr}
        </Typography>
      </Shell>
    );
  }
  if (!info) {
    return (
      <Shell>
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </Shell>
    );
  }

  const first = info.student_first;

  if (done) {
    return (
      <Shell>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5 }}>
          {done.kind === "enrolled"
            ? `${first} is already enrolled`
            : `${first}'s free trial is ready`}
        </Typography>
        <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {done.kind === "enrolled" &&
            `${first} is already enrolled in the Ardent Research Program and can sign in at scholar.ardentlabs.io.`}
          {done.kind === "sent" &&
            `We emailed ${first} at ${info.email_hint} a link to sign in and create a password. Please check that inbox, including the spam folder.`}
          {done.kind === "recent" &&
            `We emailed ${first} at ${info.email_hint} a sign-in link a few minutes ago. Please check that inbox, including the spam folder.`}
        </Typography>
      </Shell>
    );
  }

  return (
    <Shell>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        {info.activated
          ? `${first}'s free trial is already set up`
          : `Start ${first}'s free trial`}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
        {info.activated
          ? `We sent ${first} a link to sign in and create a password at ${info.email_hint}. If it did not arrive, we can send it again.`
          : `This sets up ${first}'s free trial of Ardent Scholar, with the Ardent AI Lab and Step 1 for exploring research topics. We will email ${first} at ${info.email_hint} a link to sign in and create a password.`}
      </Typography>
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}
      <Button
        variant="contained"
        size="large"
        disabled={busy}
        onClick={activate}
      >
        {busy
          ? "Setting up…"
          : info.activated
            ? "Send the sign-in email again"
            : "Start the free trial"}
      </Button>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F9F7F4",
        display: "flex",
        justifyContent: "center",
        px: 2,
        py: { xs: 4, sm: 8 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 560 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 20,
              color: "#0F2B4C",
              lineHeight: 1,
            }}
          >
            Ardent
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 2,
              color: "#C8973A",
              fontWeight: 700,
            }}
          >
            RESEARCH
          </Typography>
        </Box>
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }} elevation={0}>
          {children}
        </Paper>
      </Box>
    </Box>
  );
}
